import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useTelegram } from './context/TelegramContext';
import { useAuthStore } from './stores/authStore';
import { ErrorBoundary } from './components';
import Layout from './components/Layout';
import MobileLayout from './components/MobileLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';

// Lazy load all page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const POList = lazy(() => import('./pages/POList'));
const PODetail = lazy(() => import('./pages/PODetail'));
const EditPO = lazy(() => import('./pages/EditPO'));
const CreatePO = lazy(() => import('./pages/CreatePO'));
const Permits = lazy(() => import('./pages/Permits'));
const Payments = lazy(() => import('./pages/Payments'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Settings = lazy(() => import('./pages/Settings'));
const Emails = lazy(() => import('./pages/Emails'));
const Shipments = lazy(() => import('./pages/Shipments'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const RegulatoryDashboard = lazy(() => import('./pages/RegulatoryDashboard'));

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-gray-500">Loading...</span>
    </div>
  </div>
);

function App() {
  const { isMiniApp } = useTelegram();
  const { user } = useAuthStore();

  // Manager role gets Arabic dashboard
  const isManager = user?.role === 'manager';
  // Regulatory role gets Arabic regulatory dashboard
  const isRegulatory = user?.role === 'regulatory';

  // Use mobile layout for Telegram Mini App, desktop layout for web
  const LayoutComponent = isMiniApp ? MobileLayout : Layout;

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public route - Login */}
        <Route path="/login" element={<Login />} />

        {/* Manager dashboard - separate full-screen layout */}
        {isManager && (
          <Route path="/*" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <ManagerDashboard />
                </Suspense>
              </ErrorBoundary>
            </ProtectedRoute>
          } />
        )}

        {/* Regulatory dashboard - separate full-screen layout */}
        {isRegulatory && (
          <Route path="/*" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <RegulatoryDashboard />
                </Suspense>
              </ErrorBoundary>
            </ProtectedRoute>
          } />
        )}

        {/* Admin/User protected routes */}
        {!isManager && !isRegulatory && (
          <Route path="/" element={<ProtectedRoute><LayoutComponent /></ProtectedRoute>}>
            <Route index element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></ErrorBoundary>} />
            <Route path="pos" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><POList /></Suspense></ErrorBoundary>} />
            <Route path="pos/new" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><CreatePO /></Suspense></ErrorBoundary>} />
            <Route path="pos/:id" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><PODetail /></Suspense></ErrorBoundary>} />
            <Route path="pos/:id/edit" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><EditPO /></Suspense></ErrorBoundary>} />
            <Route path="emails" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Emails /></Suspense></ErrorBoundary>} />
            <Route path="permits" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Permits /></Suspense></ErrorBoundary>} />
            <Route path="payments" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Payments /></Suspense></ErrorBoundary>} />
            <Route path="tasks" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Tasks /></Suspense></ErrorBoundary>} />
            <Route path="shipments" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Shipments /></Suspense></ErrorBoundary>} />
            <Route path="alerts" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><AlertsPage /></Suspense></ErrorBoundary>} />
            <Route path="settings" element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Settings /></Suspense></ErrorBoundary>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        )}
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
